/**
 * Custom Field Type
 *
 * Provides custom column renderer for fully custom cell rendering.
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import type { CustomColumn, ColumnRenderer, RenderOptions } from '../../../columnModel';

// ============================================================================
// Custom Column Renderer
// ============================================================================

/**
 * Custom read-only renderer - uses provided cell and tooltipRender functions
 * Allows full customization of cell rendering
 */
export const customReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const colWithCustom = col as CustomColumn<T> & {
    cell?: (context: CellContext<T, unknown>) => React.ReactNode;
    tooltipRender?: (cell: CellContext<T, unknown>) => string | React.ReactNode;
  };

  return {
    cell: colWithCustom.cell,
    tooltipRender: colWithCustom.tooltipRender,
  };
};

/**
 * Custom editable renderer - same as read-only since custom columns
 * handle their own edit state
 */
export const customEditableRenderer = customReadOnlyRenderer;

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const customReadOnly: ColumnRenderer = customReadOnlyRenderer;

/** Editable renderer with proper typing (same as read-only for custom) */
export const customEditable: ColumnRenderer = customEditableRenderer;
